import React, {useState} from 'react';
import {Box, Button, Checkbox, FormControlLabel, Stack, Typography} from '@mui/material';
import AdmicityGroupFilterSelect from '../../../shared-components/AdmicityGroupFilterSelect';
import AdmicityTableV2 from '../../../shared-components/Table/V2/AdmicityTableV2';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import IconButton from '@mui/material/IconButton';
import {mergeArraysByKey} from '../../../utility/mergeArraysByKey';

/* eslint-disable react/prop-types */

const groupFiltersTableColumns = [
    {
        accessorKey: 'displayName',
        header: 'Group Name',
        grow: true,
    },
    {
        accessorKey: 'excluded',
        header: 'Status',
        size: 50,
        grow: false,
        Cell: ({cell}) => cell.getValue() === true ? 'Excluded' : 'Included'
    }
];

const groupFiltersTableMaxHeight = 'calc(56px + (5 * 52px))';

/**
 * Group include/exclude + strict filtering for school document profile visibility.
 * Kept separate from DownloadReportPopup/FiltersTab to avoid shared-component churn.
 */
const DocumentAudienceGroupFields = ({
    data,
    settings,
    setSettings,
    strictRecipientsFiltering,
    setStrictRecipientsFiltering,
    disableStrictRecipientsFiltering = false,
    groupFiltersLabel = 'Who can see these documents on a student profile',
    groupFilterSelectLabel = 'Groups',
}) => {
    const [filter, setFilter] = useState({});
    const isFilterNotSelected = filter == null || !filter.filterId;
    const rootFilterIds = data ? data.map(x => x.id) : [];

    const handleAddGroupFilter = (isExcluded) => {
        const newFilter = {
            id: filter.filterId,
            displayName: filter.paths.map(x => x.name).join(' - '),
            excluded: isExcluded
        };

        setSettings(prev => ({
            ...prev,
            groupFilters: mergeArraysByKey([...settings.groupFilters, newFilter], settings.groupFilters)
        }));
        setFilter(null);
    };

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="subtitle1" fontWeight="bold">{groupFiltersLabel}</Typography>
            <Box>
                <AdmicityGroupFilterSelect
                    isRequired={false}
                    sx={{width: '100%'}}
                    data={data}
                    label={groupFilterSelectLabel}
                    value={filter?.filterId || ''}
                    onChange={(filterId, paths) => setFilter({filterId, paths})}
                />
                <Stack alignItems="center" flexDirection="row" gap={1} justifyContent="end" pt={1}>
                    <Button
                        onClick={() => handleAddGroupFilter(true)}
                        variant="outlined"
                        disabled={isFilterNotSelected || rootFilterIds.includes(filter.filterId)}
                    >
                        Exclude
                    </Button>
                    <Button
                        onClick={() => handleAddGroupFilter(false)}
                        variant="contained"
                        disabled={!filter?.filterId}
                        sx={{
                            backgroundColor: '#186BBE',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#155FA7',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Include
                    </Button>
                </Stack>
            </Box>
            {settings.groupFilters?.length > 0 && (
                <Box sx={{pt: 1}}>
                    <AdmicityTableV2
                        columns={groupFiltersTableColumns}
                        data={settings.groupFilters}
                        tableProps={{
                            layoutMode: 'grid-no-grow',
                            enableRowActions: true,
                            enableSorting: false,
                            enableFilters: false,
                            enableHiding: false,
                            enableFullScreenToggle: false,
                            enableColumnResizing: false,
                            enableBottomToolbar: false,
                            enableTopToolbar: false,
                            muiTableBodyProps: {
                                sx: {
                                    '& :last-child td': {
                                        borderBottom: 0,
                                    },
                                }
                            },
                            muiTablePaperProps: {
                                variant: 'outlined',
                                elevation: 0,
                                sx: {
                                    display: 'block',
                                    flexDirection: 'unset',
                                    flexGrow: 0,
                                    width: '100%',
                                    height: 'auto',
                                }
                            },
                            muiTableContainerProps: {
                                sx: {
                                    flex: 'unset',
                                    maxHeight: groupFiltersTableMaxHeight,
                                    minHeight: 'auto',
                                    border: 'none',
                                    overflow: 'auto',
                                },
                                style: {
                                    minHeight: 'auto',
                                },
                            },
                            renderRowActions: ({row}) => (
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const filters = settings.groupFilters.filter(
                                            g => row.original.id !== g.id
                                        );

                                        setSettings(prev => ({
                                            ...prev,
                                            groupFilters: filters
                                        }));
                                    }}>
                                    <DeleteOutlineRoundedIcon fontSize="small"/>
                                </IconButton>
                            )
                        }}
                    />
                    {settings.groupFilters.length > 1 && (
                        <FormControlLabel
                            sx={{
                                pt: 1,
                                pb: 1,
                            }}
                            disabled={disableStrictRecipientsFiltering}
                            control={(
                                <Checkbox
                                    checked={strictRecipientsFiltering}
                                    onChange={(e) => setStrictRecipientsFiltering(e.target.checked)}
                                />
                            )}
                            label="Must appear in all these filters"
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default DocumentAudienceGroupFields;
